import gql from 'graphql-tag';
import { isUndefined } from 'lodash';
import {
	getDrillDownVendorData,
	getGraphQLClientCapitalMarketUpdateData,
} from 'utils/apiRequests';
import { getFormattedSpendWithSymbol } from 'utils/number';

//Below two are helper functions for fieldname errors for graphql 2.4.0
function lowercaseFirstChar(obj) {
	const newObj = {};
	for (let key in obj) {
		if (obj.hasOwnProperty(key)) {
			const value = obj[key];
			let newKey = key.charAt(0).toLowerCase() + key.slice(1);
			newKey = newKey === 'description' ? 'exploreDescription' : newKey;
			if (typeof value === 'object' && !Array.isArray(value)) {
				// Recursively lowercase first character of field names of nested objects
				newObj[newKey] = lowercaseFirstChar(value);
			} else {
				newObj[newKey] = value;
			}
		}
	}

	// Remove the "filterTo" and "dateTo" property if it exists
	delete newObj.filterTo;
	delete newObj.dateTo;

	return newObj;
}

const exploreLibraryQuery = (request, columns, data, groupedName) => {
	let {
		cteModel,
		primaryTable,
		rowGroups,
		caseModel,
		valueCols,
		joinModel,
		havingModel,
		groupCols,
	} = data;
	cteModel[0].filterModel = [lowercaseFirstChar(request.filterModel)];
	let fields = columns.map(col => col.field);
	const children = columns.reduce((acc, item) => {
		if (item.children && Array.isArray(item.children)) {
			return [...acc, ...item.children];
		}
		return acc;
	}, []);

	fields = fields.concat(children.map(col => col.field));
	return {
		query: gql`
			   query getWorkingCapitalData(
    $cteExist: Boolean = null
    $startRow: Int!
    $endRow: Int!
    $primaryTable: String!
    $sorting: [SortModelType!] = null
    $rowGroups: [RowGroupModelType!] = null
    $valueCols: [ValueColsModelType!] = null
    $groupCols: [GroupColsModelType!] = null
    $joinModel: [JoinModelType!] = null
    $filterModel: [FilterModelType!] = null
    $havingModel: [HavingModelType!] = null
    $caseModel: [CaseModelType!] = null
	$cteModel: [CTEModelType] = null
			) {
				getWorkingCapitalData(
					startRow: $startRow
					endRow: $endRow
					sorting: $sorting
					filterModel: $filterModel
                    cteExist:$cteExist
                    primaryTable:$primaryTable
                    rowGroups:$rowGroups
                    valueCols:$valueCols
                    groupCols:$groupCols
                    joinModel:$joinModel
                    havingModel:$havingModel
                    caseModel:$caseModel
					cteModel:$cteModel
				) {
					${[...fields, 'iD']}
				}
			}
		`,

		variables: {
			startRow: request.startRow || 0,
			endRow: request.endRow || 200,
			sortModel: request.sortModel,
			filterModel: {},
			sorting: request.sortModel,
			cteModel,
			primaryTable,
			rowGroups,
			caseModel,
			valueCols,
			joinModel,
			havingModel,
			groupCols,
		},
	};
};

function transformArrayWithFormattedSpend(array, currencySymbol) {
	// Check if the getFormattedSpendWithSymbol function is defined
	if (typeof getFormattedSpendWithSymbol !== 'function') {
		throw new Error('getFormattedSpendWithSymbol function is not defined.');
	}

	// Transform the array
	return array.map(item => {
		const transformedItem = { ...item };

		// List of keys to transform
		const keysToTransform = [
			'total_amount_paid',
			'working_capital_reduction',
			'working_capital_reduction_pcard',
			'harmonization_Opportunity_Term_Reduction',
		];

		// Apply transformation to each key
		keysToTransform.forEach(key => {
			if (item[key] !== undefined && item[key] !== null) {
				transformedItem[key] = getFormattedSpendWithSymbol(
					item[key],
					currencySymbol,
					true,
					true
				).toUpperCase();
			}
		});
		transformedItem.realized_terms = Math.round(transformedItem.realized_terms);
		return transformedItem;
	});
}
//this function makes columns from the data
const ServerSideDataSource = (gridOptions, columnApi) => {
	return {
		getRows: async params => {
			const tempfilterModel = {};
			if (params.request.groupKeys.length > 0) {
				// tempfilterModel['hARMONIZED_VENDOR_NAME'] = {
				// 	values: params.request.groupKeys,
				// 	filterType: 'set',
				// };
				const vendorPayload = {
					FilterList: params.request.rowGroupCols
						.slice(0, params.request.groupKeys.length + 1)
						.map(data => data.field),
					FilterValueList: params.request.groupKeys,
					dateFrom: gridOptions.dateRangeData.dateFrom,
					dateTo: gridOptions.dateRangeData.dateTo,
				};

				if (vendorPayload.FilterList.length === 4) {
					columnApi.setColumnVisible('pURCHASING_COUNTRY', true);
				} else if (vendorPayload.FilterList.length === 3) {
					columnApi.setColumnVisible('site', true);
				} else if (vendorPayload.FilterList.length === 2) {
					columnApi.setColumnVisible('vENDOR_NAME', true);
				}

				let rows = await getDrillDownVendorData(vendorPayload);
				rows = transformArrayWithFormattedSpend(rows.data, '$');

				let lastRow = -1;
				if (rows.length <= 200) {
					lastRow = params.request.startRow + rows.length;
				}
				params.successCallback(rows, lastRow);
			} else {
				if (gridOptions.dayDiff) {
					gridOptions.data.cteModel &&
						gridOptions.data.cteModel.forEach(item => {
							if (item.cteName === 'Vendor_Working_Capital_Reduction') {
								item.caseModel.forEach(item => {
									let itemCopy = item.thenCondition?.split('/');
									itemCopy[1] = gridOptions.dayDiff;
									item.thenCondition = itemCopy?.join('/');
								});
							}
						});
				}
				if (!isUndefined(gridOptions.currentBreakDownFilter)) {
					tempfilterModel[gridOptions.currentBreakDownFilter] = {
						values: gridOptions.currentFilterValuesSelected,
						filterType: 'set',
					};
				}
				if (gridOptions?.isDPOMultiplefilterData) {
					tempfilterModel[gridOptions.isDPOMultiplefilterData.key] = {
						values: gridOptions.isDPOMultiplefilterData.value,
						filterType: 'set',
					};
				}
				if (gridOptions.dateRangeData) {
					tempfilterModel['payment_Date'] = {
						operator: 'AND',
						filterType: 'date',
						condition1: {
							filterType: 'date',
							type: 'lessThan',
							dateFrom: gridOptions.dateRangeData.dateTo,
						},
						condition2: {
							filterType: 'date',
							type: 'greaterThan',
							dateFrom: gridOptions.dateRangeData.dateFrom,
						},
					};
				}
				// date:[gridOptions.selectedDate.label]

				const object = {};
				object.gridOptions = gridOptions;
				object.client = await getGraphQLClientCapitalMarketUpdateData();
				const columns = gridOptions.data.columnDefs;
				params.request.filterModel = tempfilterModel;
				params.request.sortModel = [
					{
						colId: '2',
						sort: 'desc',
					},
				];
				object.client
					.query(
						exploreLibraryQuery(
							params.request,
							columns,
							gridOptions.data,
							gridOptions.data.currentGroupOpened
						)
					)
					.then(response => {
						let rows = transformArrayWithFormattedSpend(
							response.data.getWorkingCapitalData,
							'$'
						);

						let lastRow = -1;
						if (rows.length <= 200) {
							lastRow = params.request.startRow + rows.length;
						}
						params.successCallback(rows, lastRow);
					})

					.catch(err => {
						console.error(err);
						params.failCallback();
					});
			}
		},
	};
};

export default ServerSideDataSource;
